// ServicioDetalle.tsx — Co Marca — hero centrado estilo AboutusProjects / Services
import './PublicCatalogTheme.css';
import React, { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { fetchPublicService, PublicService as Servicio, PublicPrice as PrecioPlanServicio } from '../../../services/publicCatalogService';
import { usePublicCatalogSeo } from '../../../hooks/usePublicCatalogSeo';
import {
    ArrowLeft, ChevronRight, Home, Star,
    Tag, Zap, MessageCircle, ArrowRight, Info, PackageX, Shield, Clock, Users,
} from 'lucide-react';
import BrandStripes from '../../../components/common/BrandStripes';
import {PublicServiceSpaces} from '../../../components/common/PublicServiceSpaces';

const colors = {
    primary: '#c1348b',
    primaryDark: '#8f1f64',
    secondary: '#61257b',
    secondaryLight: '#8b3fa8',
    accent: '#ffd97c',
    accentDark: '#ea8406',
};

const css = `
@import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600;700;800;900&display=swap');
* { box-sizing: border-box; }
@keyframes blobFloat {
    0%,100% { border-radius:60% 40% 30% 70%/60% 30% 70% 40%; }
    50%      { border-radius:30% 60% 70% 40%/50% 60% 30% 60%; }
}
@keyframes sdFadeUp {
    from { opacity:0; transform:translateY(24px); }
    to   { opacity:1; transform:translateY(0); }
}
@keyframes pulseD {
    0%,100% { box-shadow:0 0 0 0 rgba(193, 52, 139,.35); }
    50%     { box-shadow:0 0 0 6px rgba(193, 52, 139,0); }
}
@keyframes spin { to { transform:rotate(360deg); } }
.blob-sd  { animation: blobFloat 9s ease-in-out infinite; }
.sd-back:hover  { background:rgba(97, 37, 123,.08) !important; color:var(--catalog-accent, #61257b) !important; border-color:rgba(97, 37, 123,.15) !important; }
.sd-cta:hover   { transform:translateY(-2px) !important; box-shadow:0 10px 28px rgba(193, 52, 139,.28) !important; }
.fade-up { opacity:0; transform:translateY(28px); transition:opacity .75s ease, transform .75s cubic-bezier(.16,1,.3,1); }
.fade-up.in { opacity:1; transform:translateY(0); }
.fade-up.d1 { transition-delay:.1s; }
.fade-up.d2 { transition-delay:.2s; }
.fade-up.d3 { transition-delay:.3s; }
.fade-up.d4 { transition-delay:.4s; }
@media (max-width:680px) {
    .plan-row { flex-direction:column !important; }
    .plan-left { flex:none !important; min-height:160px !important; width:100% !important; }
}
`;

function useInView(): [React.RefObject<HTMLDivElement>, boolean] {
    const ref = useRef<HTMLDivElement>(null);
    const [inView, setInView] = useState(false);
    useEffect(() => {
        if (ref.current) {
            const r = ref.current.getBoundingClientRect();
            if (r.top < window.innerHeight) { setInView(true); return; }
        }
        const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setInView(true); }, { threshold: 0.08 });
        if (ref.current) obs.observe(ref.current);
        return () => obs.disconnect();
    }, []);
    return [ref, inView];
}

// ══ PLAN CARD — layout horizontal, imagen/color izq + info der ══
function PlanCard({ plan, index }: { plan: PrecioPlanServicio; index: number }) {
    const [ref, inView] = useInView();
    const [hov, setHov] = useState(false);
    const isPremium = plan.plan_nombre.toLowerCase().includes('premium');
    const accentList = [colors.primary, colors.secondary, colors.accentDark, colors.secondaryLight, colors.primaryDark];
    const accent = accentList[index % accentList.length];

    return (
        <div
            ref={ref}
            className="plan-row"
            onMouseEnter={() => setHov(true)}
            onMouseLeave={() => setHov(false)}
            style={{
                display: 'flex', flexDirection: 'row', flexWrap: 'wrap',
                background: 'var(--catalog-surface)', borderRadius: '24px',
                border: hov ? `1.5px solid ${accent}35` : '1.5px solid var(--catalog-border)',
                boxShadow: hov ? `0 20px 56px ${accent}14, 0 6px 20px rgba(0,0,0,.06)` : '0 2px 16px rgba(0,0,0,.05)',
                overflow: 'hidden',
                transition: 'all .35s cubic-bezier(.16,1,.3,1)',
                transform: inView ? (hov ? 'translateY(-4px)' : 'translateY(0)') : 'translateY(32px)',
                opacity: inView ? 1 : 0,
                transitionDelay: `${index * .07}s`,
                cursor: 'default',
            }}
        >
            {/* ── Lado color izq ── */}
            <div
                className="plan-left"
                style={{
                    flex: '0 0 clamp(200px,30%,320px)',
                    minHeight: '220px',
                    position: 'relative', overflow: 'hidden',
                    background: hov
                        ? `linear-gradient(145deg,${accent},${accent}cc)`
                        : `linear-gradient(145deg,${accent}1a,${accent}0a)`,
                    display: 'flex', flexDirection: 'column',
                    alignItems: 'center', justifyContent: 'center',
                    transition: 'background .35s cubic-bezier(.16,1,.3,1)',
                    padding: '32px 20px',
                }}
            >
                {/* Círculos decorativos */}
                <div style={{ position: 'absolute', top: '-40px', right: '-40px', width: '150px', height: '150px', borderRadius: '50%', background: 'rgba(255,255,255,.07)', pointerEvents: 'none' }} />
                <div style={{ position: 'absolute', bottom: '-28px', left: '-28px', width: '110px', height: '110px', borderRadius: '50%', background: 'rgba(255,255,255,.05)', pointerEvents: 'none' }} />

                {/* Overlay gradiente derecho → blanco al hover */}
                <div style={{ position: 'absolute', inset: 0, background: hov ? 'transparent' : `linear-gradient(to right, transparent 65%, var(--catalog-surface) 100%)`, transition: 'background .35s', pointerEvents: 'none' }} />

                {/* Icon box */}
                <div style={{
                    width: '54px', height: '54px', borderRadius: '16px',
                    background: hov ? 'rgba(255,255,255,.22)' : `${accent}14`,
                    border: hov ? '1.5px solid rgba(255,255,255,.3)' : `1.5px solid ${accent}28`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    marginBottom: '18px', transition: 'all .3s',
                    boxShadow: hov ? '0 6px 18px rgba(0,0,0,.14)' : 'none',
                    position: 'relative',
                }}>
                    <Tag size={23} color={hov ? '#fff' : `var(--catalog-accent, ${accent})`} strokeWidth={1.8} />
                </div>

                {/* Precio */}
                <div style={{ textAlign: 'center', position: 'relative' }}>
                    <div style={{ fontSize: '.6rem', fontWeight: '700', letterSpacing: '.1em', textTransform: 'uppercase', color: hov ? 'rgba(255,255,255,.65)' : 'var(--catalog-subtle, #94a3b8)', marginBottom: '4px', transition: 'color .3s' }}>
                        Precio inicial
                    </div>
                    <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'center', gap: '3px' }}>
                        <span style={{ fontSize: '2.8rem', fontWeight: '900', lineHeight: 1, letterSpacing: '-.04em', color: hov ? '#fff' : 'var(--catalog-text, #0f172a)', transition: 'color .3s', fontFamily: "'DM Sans',sans-serif" }}>
                            {plan.precio_inicial}
                        </span>
                        <span style={{ fontSize: '.78rem', fontWeight: '700', paddingBottom: '7px', color: hov ? 'rgba(255,255,255,.65)' : 'var(--catalog-subtle, #94a3b8)', transition: 'color .3s' }}>Bs.</span>
                    </div>
                    {plan.precio_posterior && (
                        <div style={{ marginTop: '8px', fontSize: '.68rem', fontWeight: '600', color: hov ? 'rgba(255,255,255,.6)' : 'var(--catalog-subtle, #94a3b8)', transition: 'color .3s' }}>
                            → {plan.precio_posterior} Bs. /periodo
                        </div>
                    )}
                </div>

                {/* Barra acento bottom al hover */}
                <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: '4px', background: `linear-gradient(90deg,${accent},${accent}88)`, opacity: hov ? 1 : 0, transition: 'opacity .3s' }} />
            </div>

            {/* ── Lado derecho: contenido ── */}
            <div style={{ flex: 1, minWidth: '240px', padding: 'clamp(28px,3.5vw,40px) clamp(24px,3.5vw,40px)', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>

                {/* Tag + badge premium */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '14px' }}>
                    <span style={{ fontSize: '.68rem', fontWeight: '700', letterSpacing: '.14em', textTransform: 'uppercase', color: `var(--catalog-accent, ${accent})` }}>Plan</span>
                    <div style={{ height: '2px', width: '26px', borderRadius: '2px', background: accent }} />
                    {isPremium && (
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '3px', background: `${colors.accentDark}12`, border: `1px solid ${colors.accentDark}22`, borderRadius: '6px', padding: '2px 9px', fontSize: '.6rem', fontWeight: '800', color: `var(--catalog-accent, ${colors.accentDark})`, textTransform: 'uppercase', letterSpacing: '.07em' }}>
                            <Star size={8} fill={colors.accentDark} strokeWidth={0} /> Premium
                        </span>
                    )}
                </div>

                {/* Nombre del plan */}
                <h3 style={{ fontSize: 'clamp(1.25rem,2.2vw,1.65rem)', fontWeight: '800', color: 'var(--catalog-text, #0f172a)', lineHeight: 1.2, letterSpacing: '-.02em', marginBottom: '14px', fontFamily: "'DM Sans',sans-serif" }}>
                    {plan.plan_nombre}
                </h3>

                {/* Divider coloreado */}
                <div style={{ height: '2px', background: `linear-gradient(to right,${accent}25,transparent)`, marginBottom: '18px' }} />

                {/* Info precio posterior */}
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '24px' }}>
                    {plan.precio_posterior ? (
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', background: `${accent}08`, border: `1px solid ${accent}20`, borderRadius: '9px', padding: '6px 13px', fontSize: '.75rem', fontWeight: '700', color: `var(--catalog-accent, ${accent})` }}>
                            <ArrowRight size={11} strokeWidth={2.5} />
                            Periodo siguiente: {plan.precio_posterior} Bs.
                        </span>
                    ) : (
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', background: `${colors.secondaryLight}08`, border: `1px solid ${colors.secondaryLight}20`, borderRadius: '9px', padding: '6px 13px', fontSize: '.75rem', fontWeight: '700', color: `var(--catalog-accent, ${colors.secondaryLight})` }}>
                            Precio fijo por periodo
                        </span>
                    )}
                </div>

                {/* CTA — se desplaza al hover */}
                <div>
                    <span style={{
                        display: 'inline-flex', alignItems: 'center', gap: '8px',
                        background: `linear-gradient(135deg,${accent},${accent}cc)`,
                        color: '#fff', borderRadius: '12px',
                        padding: '11px 24px', fontSize: '.86rem', fontWeight: '700',
                        fontFamily: "'DM Sans',sans-serif",
                        boxShadow: `0 4px 14px ${accent}35`,
                        transition: 'all .25s cubic-bezier(.16,1,.3,1)',
                        transform: hov ? 'translateX(5px)' : 'translateX(0)',
                        cursor: 'default',
                    }}>
                        <Zap size={14} strokeWidth={2.5} /> Consultar disponibilidad <ArrowRight size={13} strokeWidth={2.5} />
                    </span>
                </div>
            </div>
        </div>
    );
}

// ══ LOADING / ERROR ══
const LoadingSpinner = () => (
    <div className="public-catalog" style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '14px', fontFamily: "'DM Sans',sans-serif", background: 'var(--catalog-page)' }}>
        <div style={{ width: '44px', height: '44px', borderRadius: '50%', border: `3px solid ${colors.primary}18`, borderTop: `3px solid ${colors.primary}`, animation: 'spin .7s linear infinite' }} />
        <style>{`@keyframes spin { to { transform:rotate(360deg); } }`}</style>
    </div>
);

// ══ MAIN ══
const ServicioDetalle = () => {
    const { categoryId, servicioId, slug } = useParams<{ categoryId: string; servicioId: string; slug: string }>();
    const navigate = useNavigate();
    const [servicio, setServicio] = useState<Servicio | null>(null);
    const [planes, setPlanes] = useState<PrecioPlanServicio[]>([]);
    const [loading, setLoading] = useState(true);
    const [vis, setVis] = useState(false);

    useEffect(() => { const t = setTimeout(() => setVis(true), 80); return () => clearTimeout(t); }, []);

    usePublicCatalogSeo(servicio);
    useEffect(() => {
        let active = true;
        setLoading(true);
        setServicio(null);
        setPlanes([]);
        fetchPublicService(slug || servicioId || '', !slug).then(data => {
            if (!active) return;
            setServicio(data);
            setPlanes(data.planes);
            if (!slug) navigate(`/servicio/${data.slug}`, {replace: true});
        }).catch(() => {if (active) setServicio(null);})
            .finally(() => {if (active) setLoading(false);});
        return () => {active = false;};
    }, [slug, servicioId, navigate]);

    if (loading) return <LoadingSpinner />;

    if (!servicio) return (
        <div className="public-catalog" style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: "'DM Sans',sans-serif" }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', background: `${colors.primary}08`, border: `1.5px solid ${colors.primary}22`, borderRadius: '14px', padding: '18px 24px' }}>
                <Info size={18} color={colors.primary} />
                <span style={{ fontSize: '.88rem', fontWeight: '700', color: `var(--catalog-accent, ${colors.primaryDark})` }}>Servicio no encontrado</span>
            </div>
        </div>
    );

    const precioDesde = planes.length > 0
        ? Math.min(...planes.map((p: PrecioPlanServicio) => Number(p.precio_inicial)))
        : null;

    return (
        <div className="public-catalog" style={{ fontFamily: "'DM Sans',sans-serif", background: 'var(--catalog-page)', minHeight: '100vh', overflowX: 'clip' }}>
            <style>{css}</style>

            {/* ════════════════════════════════
                HERO — fondo gradiente rosa,
                centrado, mismo que Services
            ════════════════════════════════ */}
            <section style={{
                background: 'var(--catalog-hero)',
                padding: 'clamp(100px,14vw,140px) 32px clamp(60px,8vw,80px)',
                position: 'relative', overflow: 'hidden',
            }}>
                {/* Blob animado arriba derecha */}
                <div className="blob-sd" style={{ position: 'absolute', top: '-80px', right: '-80px', width: '420px', height: '420px', background: `linear-gradient(135deg,${colors.primary}14,${colors.secondary}0e)`, pointerEvents: 'none' }} />
                {/* Puntos decorativos izq */}
                <div style={{ position: 'absolute', top: '20%', left: '4%', opacity: .09, display: 'grid', gridTemplateColumns: 'repeat(5,1fr)', gap: '14px', pointerEvents: 'none' }}>
                    {[...Array(25)].map((_, i) => <div key={i} style={{ width: '5px', height: '5px', borderRadius: '50%', background: colors.primaryDark }} />)}
                </div>

                <div style={{ maxWidth: '1200px', margin: '0 auto', textAlign: 'center', position: 'relative' }}>

                    {/* Breadcrumb centrado */}
                    <nav className={`fade-up ${vis ? 'in' : ''}`} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', fontSize: '.75rem', fontWeight: '600', color: 'var(--catalog-subtle, #94a3b8)', marginBottom: '28px', flexWrap: 'wrap' }}>
                        {[
                            { label: 'Inicio', href: '/', Icon: Home },
                            { label: 'Servicios', href: '/servicios/cowork', Icon: null },
                            { label: servicio.categoria_nombre || '...', href: `/servicios/categoria/${servicio.categoria_slug}`, Icon: null },
                            { label: servicio.nombre, href: null, Icon: null },
                        ].map((item, i) => (
                            <React.Fragment key={i}>
                                {i > 0 && <ChevronRight size={11} color="#e2e8f0" />}
                                {item.href ? (
                                    <a href={item.href} style={{ display: 'flex', alignItems: 'center', gap: '3px', color: 'var(--catalog-subtle, #94a3b8)', textDecoration: 'none', transition: 'color .2s' }}
                                        onMouseEnter={e => { e.currentTarget.style.color = `var(--catalog-accent, ${colors.primary})`; }}
                                        onMouseLeave={e => { e.currentTarget.style.color = 'var(--catalog-subtle, #94a3b8)'; }}>
                                        {item.Icon && <item.Icon size={11} />} {item.label}
                                    </a>
                                ) : (
                                    <span style={{ color: `var(--catalog-accent, ${colors.primary})`, fontWeight: '700' }}>{item.label}</span>
                                )}
                            </React.Fragment>
                        ))}
                    </nav>

                    {/* Badge pulsante */}
                    <div className={`fade-up ${vis ? 'in' : ''}`} style={{ marginBottom: '18px' }}>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', background: `${colors.primary}14`, border: `1.5px solid ${colors.primary}28`, borderRadius: '100px', padding: '6px 18px', fontSize: '.78rem', fontWeight: '700', color: `var(--catalog-accent, ${colors.primaryDark})`, letterSpacing: '.1em', textTransform: 'uppercase' }}>
                            <span style={{ width: '7px', height: '7px', borderRadius: '50%', background: colors.primary, animation: 'pulseD 2s infinite', display: 'inline-block' }} />
                            Co Marca Group
                        </span>
                    </div>

                    {/* Título grande */}
                    <h1 className={`fade-up d1 ${vis ? 'in' : ''}`} style={{ fontSize: 'clamp(2.2rem,4.5vw,3.6rem)', fontWeight: '900', color: 'var(--catalog-text, #0f172a)', lineHeight: 1.08, letterSpacing: '-.025em', marginBottom: '16px' }}>
                        {servicio.nombre.split(' ').length > 2
                            ? (<>{servicio.nombre.split(' ').slice(0, Math.ceil(servicio.nombre.split(' ').length / 2)).join(' ')}{' '}
                                <span style={{ background: 'var(--catalog-title-gradient)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
                                    {servicio.nombre.split(' ').slice(Math.ceil(servicio.nombre.split(' ').length / 2)).join(' ')}
                                </span></>)
                            : (<><span style={{ background: 'var(--catalog-title-gradient)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>{servicio.nombre}</span></>)
                        }
                    </h1>

                    {/* Descripción */}
                    <p className={`fade-up d2 ${vis ? 'in' : ''}`} style={{ fontSize: '1.02rem', color: 'var(--catalog-muted, #64748b)', maxWidth: '540px', margin: '0 auto 24px', lineHeight: 1.72, fontWeight: '400' }}>
                        {servicio.descripcion}
                    </p>

                    {/* Feature pills */}
                    <div className={`fade-up d2 ${vis ? 'in' : ''}`} style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: '8px', marginBottom: '28px' }}>
                        {([
                            { icon: Users, label: 'Múltiples personas', color: colors.secondary },
                            { icon: Shield, label: 'Espacio seguro', color: colors.secondaryLight },
                            { icon: Clock, label: 'Horario flexible', color: colors.accentDark },
                        ] as { icon: React.ElementType; label: string; color: string }[]).map(({ icon: Icon, label, color }) => (
                            <span key={label} style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', background: `${color}09`, border: `1px solid ${color}18`, borderRadius: '8px', padding: '5px 12px', fontSize: '.72rem', fontWeight: '700', color: `var(--catalog-accent, ${color})`, backdropFilter: 'blur(4px)' }}>
                                <Icon size={11} strokeWidth={2.5} /> {label}
                            </span>
                        ))}
                    </div>

                    {/* Stats + precio desde */}
                    <div className={`fade-up d3 ${vis ? 'in' : ''}`} style={{ display: 'inline-flex', background: 'var(--catalog-surface)', borderRadius: '18px', border: '1.5px solid var(--catalog-border)', boxShadow: '0 2px 16px rgba(0,0,0,.05)', overflow: 'hidden', marginBottom: '28px' }}>
                        {[
                            { value: planes.length.toString(), label: 'Planes', color: colors.primary },
                            ...(precioDesde !== null ? [{ value: `${precioDesde} Bs.`, label: 'Desde', color: colors.secondary }] : []),
                            { value: servicio.requiere_espacio ? 'Sí' : 'No', label: 'Req. espacio', color: colors.secondaryLight },
                        ].map((s, i, arr) => (
                            <div key={s.label} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '16px 24px', borderRight: i < arr.length - 1 ? '1.5px solid var(--catalog-border)' : 'none' }}>
                                <div style={{ fontSize: '1.55rem', fontWeight: '900', color: `var(--catalog-accent, ${s.color})`, lineHeight: 1, letterSpacing: '-.03em', fontFamily: "'DM Sans',sans-serif" }}>{s.value}</div>
                                <div style={{ fontSize: '.62rem', color: 'var(--catalog-subtle, #94a3b8)', fontWeight: '600', marginTop: '4px' }}>{s.label}</div>
                            </div>
                        ))}
                    </div>

                    {/* Barra de colores + botón volver */}
                    <div className={`fade-up d3 ${vis ? 'in' : ''}`} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '16px' }}>
                        {/* Botón volver */}
                        <button className="sd-back" onClick={() => navigate(-1)} style={{ display: 'inline-flex', alignItems: 'center', gap: '7px', background: 'var(--catalog-page)', border: '1.5px solid var(--catalog-border)', borderRadius: '10px', padding: '8px 16px', fontSize: '.78rem', fontWeight: '700', color: 'var(--catalog-muted, #64748b)', cursor: 'pointer', transition: 'all .2s', fontFamily: "'DM Sans',sans-serif" }}>
                            <ArrowLeft size={13} /> Volver
                        </button>
                        {/* Barra colores */}
                        <BrandStripes size="sm" />
                    </div>

                    {/* Imagen del servicio si existe — debajo del hero centrado */}
                    {servicio.imagen && (
                        <div className={`fade-up d4 ${vis ? 'in' : ''}`} style={{ marginTop: '40px', borderRadius: '24px', overflow: 'hidden', maxHeight: '380px', boxShadow: '0 16px 52px rgba(193, 52, 139,.08), 0 4px 20px rgba(0,0,0,.06)', border: '1.5px solid var(--catalog-border)', position: 'relative' }}>
                            <img src={servicio.imagen} alt={servicio.nombre}
                                style={{ width: '100%', height: '380px', objectFit: 'cover' }}
                                onError={e => { (e.target as HTMLImageElement).parentElement!.style.display = 'none'; }}
                            />
                            <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to top, rgba(15,23,42,.35) 0%, transparent 50%)', pointerEvents: 'none' }} />
                            {precioDesde !== null && (
                                <div style={{ position: 'absolute', bottom: '20px', left: '24px', background: 'rgba(15,23,42,.78)', backdropFilter: 'blur(10px)', borderRadius: '14px', padding: '12px 20px' }}>
                                    <p style={{ fontSize: '.58rem', fontWeight: '700', color: 'rgba(255,255,255,.55)', textTransform: 'uppercase', letterSpacing: '.09em', marginBottom: '3px' }}>Desde</p>
                                    <p style={{ fontSize: '1.6rem', fontWeight: '900', color: '#fff', letterSpacing: '-.03em', fontFamily: "'DM Sans',sans-serif" }}>
                                        {precioDesde} <span style={{ fontSize: '.7rem', fontWeight: '600', opacity: .6 }}>Bs.</span>
                                    </p>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </section>

            <PublicServiceSpaces service={servicio} />
            {/* ════════ PLANES ════════ */}
            <section id="planes-del-servicio" style={{ scrollMarginTop: '110px', padding: 'clamp(48px,7vw,72px) clamp(20px,4vw,40px) clamp(60px,9vw,100px)', maxWidth: '1200px', margin: '0 auto' }}>

                {/* Header */}
                <div style={{ animation: 'sdFadeUp .5s cubic-bezier(.16,1,.3,1) .05s both' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '14px', marginBottom: '8px' }}>
                        <div style={{ width: '38px', height: '38px', borderRadius: '12px', background: `${colors.primary}10`, border: `1.5px solid ${colors.primary}18`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <Tag size={18} color={colors.primary} strokeWidth={1.8} />
                        </div>
                        <h2 style={{ fontSize: 'clamp(1.5rem,2.8vw,2.1rem)', fontWeight: '900', color: 'var(--catalog-text, #0f172a)', letterSpacing: '-.02em', fontFamily: "'DM Sans',sans-serif" }}>
                            Planes disponibles
                        </h2>
                    </div>
                    <p style={{ fontSize: '.9rem', color: 'var(--catalog-muted, #64748b)', fontWeight: '500', marginLeft: '52px', marginBottom: '32px' }}>
                        Elige cómo quieres disfrutar de {servicio.nombre}. Conoce las opciones y encuentra tu plan ideal.
                    </p>
                </div>

                {planes.length === 0 ? (
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '64px 20px', background: 'var(--catalog-surface)', borderRadius: '20px', border: '1.5px dashed var(--catalog-border)', gap: '12px' }}>
                        <PackageX size={28} color={colors.accentDark} strokeWidth={1.5} />
                        <p style={{ fontSize: '1rem', fontWeight: '800', color: 'var(--catalog-text, #374151)' }}>Planes próximamente</p>
                        <p style={{ fontSize: '.82rem', color: 'var(--catalog-subtle, #94a3b8)', textAlign: 'center', maxWidth: '280px', lineHeight: 1.6 }}>Contáctanos para conocer opciones personalizadas.</p>
                    </div>
                ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                        {planes.map((plan, i) => <PlanCard key={`${plan.plan_nombre}-${i}`} plan={plan} index={i} />)}
                    </div>
                )}

                {/* Barra decorativa */}
                <div style={{ marginTop: '60px', display: 'flex', justifyContent: 'center' }}>
                    <BrandStripes size="md" />
                </div>

                {/* CTA Banner */}
                <div style={{ position: 'relative', overflow: 'hidden', borderRadius: '24px', marginTop: '48px', background: `linear-gradient(135deg,${colors.primaryDark},${colors.primary} 60%,#ff6b8a)`, padding: 'clamp(32px,5vw,52px) clamp(28px,4vw,48px)', boxShadow: `0 16px 52px ${colors.primary}22` }}>
                    <div className="blob-sd" style={{ position: 'absolute', top: '-50px', right: '-50px', width: '260px', height: '260px', pointerEvents: 'none', background: 'rgba(255,255,255,.06)' }} />
                    <div style={{ position: 'relative', maxWidth: '520px', margin: '0 auto', textAlign: 'center' }}>
                        <div style={{ width: '48px', height: '48px', borderRadius: '14px', background: 'rgba(255,255,255,.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 14px' }}>
                            <Zap size={22} color="#fff" strokeWidth={1.8} />
                        </div>
                        <h2 style={{ fontSize: 'clamp(1.4rem,2.8vw,2rem)', fontWeight: '900', color: '#fff', letterSpacing: '-.03em', lineHeight: 1.1, marginBottom: '10px' }}>¿Listo para reservar?</h2>
                        <p style={{ fontSize: '.92rem', color: 'rgba(255,255,255,.8)', fontWeight: '500', lineHeight: 1.65, marginBottom: '22px' }}>Contacta a uno de nuestros asesores y comienza hoy mismo.</p>
                        <button className="sd-cta" style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', background: 'var(--catalog-surface)', color: `var(--catalog-accent, ${colors.primary})`, padding: '12px 26px', borderRadius: '12px', border: 'none', fontSize: '.88rem', fontWeight: '800', cursor: 'pointer', fontFamily: "'DM Sans',sans-serif", boxShadow: '0 4px 16px rgba(0,0,0,.1)', transition: 'all .25s cubic-bezier(.16,1,.3,1)' }}>
                            <MessageCircle size={15} strokeWidth={2} /> Hablar con un asesor <ArrowRight size={14} strokeWidth={2.5} />
                        </button>
                    </div>
                </div>

            </section>
        </div>
    );
};

export default ServicioDetalle;